import {
  paintCeiling,
  paintFloor,
  setJumpCell,
  JUMP_GRID_ROWS,
  JUMP_GROUND_ROWS,
  JUMP_TILE,
  type JumpCharGrid,
} from '../../jumpGrid.js';

/**
 * The two LONE-spike heights, in CELLS. The height tells the player which jump
 * to use, and each obstacle is apex-shifted by THAT jump's lead so an on-beat
 * press of the RIGHT size crests inside the perfect zone:
 *   - LOW (1 cell) → short hop (apex clearance ≈ 39 units).
 *   - TALL (3 cells) → full jump (apex clearance ≈ 42 units).
 * A 2-cell spike is deliberately impossible to emit — neither jump apexes in the
 * zone over it — so the grader's `perfect` band stays meaningful. Spike-walls
 * stay LOW (their WIDTH forces the full jump; a tall wide wall can't be threaded
 * on the descending arc).
 */
export const LOW_SPIKE_CELLS = 1;
export const TALL_SPIKE_CELLS = 3;

/**
 * Saw blade height in cells. At 3 cells its tip sits on the TALL-spike line, so
 * the full jump that clears it apexes inside the perfect zone too. A saw stands
 * ON the floor here (jump-over only — never ducked), the safe subset of the
 * two-sided hazard.
 */
export const SAW_CELLS = 3;

/**
 * Corridor ceiling thickness (rows from the top). Tuned so the lethal underside
 * sits between the full-jump apex (cracks it) and the short-hop apex (fits
 * beneath), forcing the short hop over the spike below.
 */
export const CORRIDOR_CEIL_ROWS = 8;

/** Raised-platform / step height band in CELLS above the baseline floor. */
export const PLATFORM_MIN_CELLS = 1;
export const PLATFORM_MAX_CELLS = 3;

/** Pit width band in COLUMNS — kept under the full-jump reach so a leap clears. */
export const PIT_MIN_COLS = 2;
export const PIT_MAX_COLS = 4;

/** Run-up / landing margin (beats) kept clear of obstacles on either side of a pit. */
export const PIT_EDGE_MARGIN_BEATS = 1.4;

/** Baseline floor's top row index — obstacles stand on the cell just above it. */
const FLOOR_TOP_ROW = JUMP_GRID_ROWS - JUMP_GROUND_ROWS;

/** A bounded rectangular span of grid columns a pattern stamped into. */
export interface ColSpan {
  startCol: number;
  endCol: number;
}

/**
 * The grid-editing surface every pattern paints through. It owns the one piece
 * of policy that keeps Jump in time with the music — the **apex shift**: an
 * obstacle is placed at the column where a press ON its onset beat puts the
 * intended jump's apex over it (short-hop spikes shift by `shortApexLead`,
 * everything the full jump clears by `longApexLead`). Patterns therefore speak
 * only in *press beats*; the stamper resolves columns, clamps to the playable
 * bounds (column 0 is the reserved spawn, the last column the chart end), and
 * exposes clear-strip probes so a pattern never paints over its neighbour.
 *
 * Heights, widths and tile kinds are centralised here so the perfect-zone
 * contract lives in exactly one place.
 */
export class JumpStamper {
  private readonly floorTopRow = FLOOR_TOP_ROW;

  constructor(
    private readonly grid: JumpCharGrid,
    private readonly cols: number,
    private readonly totalBeats: number,
    private readonly cellBeats: number,
    private readonly longApexLead: number,
    private readonly shortApexLead: number,
  ) {}

  /** Column whose centre a full-jump press at `pressBeat` apexes over. */
  fullPressCol(pressBeat: number): number {
    return Math.round((pressBeat + this.longApexLead) / this.cellBeats);
  }

  /** Column whose centre a short-hop press at `pressBeat` apexes over. */
  shortPressCol(pressBeat: number): number {
    return Math.round((pressBeat + this.shortApexLead) / this.cellBeats);
  }

  /** Beat covered by the centre of a column (a spike's judgement beat). */
  colCentreBeat(col: number): number {
    return (col + 0.5) * this.cellBeats;
  }

  /** A single column is placeable: past the spawn, in-grid, judged within the chart. */
  canPlaceCol(col: number): boolean {
    if (col < 1 || col >= this.cols) {
      return false;
    }
    const centre = this.colCentreBeat(col);
    return centre >= 0 && centre <= this.totalBeats;
  }

  /** Every column in `[startCol, endCol)` is placeable. */
  canPlaceSpan(startCol: number, endCol: number): boolean {
    if (startCol < 1 || endCol > this.cols || endCol <= startCol) {
      return false;
    }
    return this.canPlaceCol(startCol) && this.canPlaceCol(endCol - 1);
  }

  /** True iff every column in `[startCol, endCol)` is still plain baseline floor. */
  isClearStrip(startCol: number, endCol: number): boolean {
    for (let col = startCol; col < endCol; col += 1) {
      if (col < 0 || col >= this.cols) {
        return false;
      }
      for (let row = 0; row < this.floorTopRow; row += 1) {
        if (this.grid[row][col] !== JUMP_TILE.air) {
          return false;
        }
      }
      for (let r = 0; r < JUMP_GROUND_ROWS; r += 1) {
        if (this.grid[JUMP_GRID_ROWS - 1 - r][col] !== JUMP_TILE.floor) {
          return false;
        }
      }
    }
    return true;
  }

  /** True iff no column in `[startCol, endCol)` already carries a ceiling. */
  isClearAbove(startCol: number, endCol: number): boolean {
    for (let col = Math.max(0, startCol); col < Math.min(this.cols, endCol); col += 1) {
      if (this.grid[0][col] !== JUMP_TILE.air) {
        return false;
      }
    }
    return true;
  }

  /** Paint a `cells`-tall stack of `tile` standing on the baseline floor in `col`. */
  private stackOnFloor(col: number, cells: number, tile: string): void {
    for (let r = 0; r < cells; r += 1) {
      setJumpCell(this.grid, this.floorTopRow - 1 - r, col, tile);
    }
  }

  /**
   * A LOW (1-cell) ground spike — the short-hop obstacle. Returns the column it
   * landed in, or `null` if the apex-shifted column is out of bounds.
   */
  lowSpike(pressBeat: number): number | null {
    const col = this.shortPressCol(pressBeat);
    if (!this.canPlaceCol(col)) {
      return null;
    }
    this.stackOnFloor(col, LOW_SPIKE_CELLS, JUMP_TILE.spikeUp);
    return col;
  }

  /** A TALL (3-cell) ground spike — the full-jump obstacle. */
  tallSpike(pressBeat: number): number | null {
    const col = this.fullPressCol(pressBeat);
    if (!this.canPlaceCol(col)) {
      return null;
    }
    this.stackOnFloor(col, TALL_SPIKE_CELLS, JUMP_TILE.spikeUp);
    return col;
  }

  /**
   * A LOW spike-WALL `width` columns wide (each column an independent 1-cell
   * spike) — one centred full jump clears it. Returns the leading column or
   * `null`. Width is trimmed to whatever still fits the chart bounds.
   */
  spikeWall(pressBeat: number, width: number): number | null {
    const startCol = this.fullPressCol(pressBeat);
    if (!this.canPlaceCol(startCol)) {
      return null;
    }
    let placed = 0;
    for (let c = 0; c < width; c += 1) {
      const col = startCol + c;
      if (!this.canPlaceCol(col)) {
        break;
      }
      this.stackOnFloor(col, LOW_SPIKE_CELLS, JUMP_TILE.spikeUp);
      placed += 1;
    }
    return placed > 0 ? startCol : null;
  }

  /**
   * A floor-standing saw (jump-over). Only paints onto a clear strip so it never
   * collides with adjacent terrain. Returns the column or `null`.
   */
  saw(pressBeat: number): number | null {
    const col = this.fullPressCol(pressBeat);
    if (!this.canPlaceCol(col) || !this.isClearStrip(col, col + 1)) {
      return null;
    }
    this.stackOnFloor(col, SAW_CELLS, JUMP_TILE.saw);
    return col;
  }

  /**
   * A LOW spike capped by a short corridor ceiling — the full jump cracks the
   * roof, so only a short hop threads over the spike. Returns the spike column
   * or `null` (placing the spike anyway if the roof can't fit).
   */
  corridorOverLowSpike(pressBeat: number): number | null {
    const col = this.lowSpike(pressBeat);
    if (col === null) {
      return null;
    }
    const ceilStart = col - 1;
    const ceilEnd = col + 2;
    if (this.isClearAbove(ceilStart, ceilEnd)) {
      paintCeiling(this.grid, Math.max(0, ceilStart), Math.min(this.cols, ceilEnd), CORRIDOR_CEIL_ROWS);
    }
    return col;
  }

  /**
   * Raise the floor to `heightCells` above the baseline across a span, clamped
   * to bounds. The leading (left) edge becomes a lethal block-face the cube must
   * land on top of; the trailing edge drops back down (a safe walk-off) unless a
   * caller paints further terrain past it. Returns the painted span or `null`.
   */
  platform(pressBeat: number, heightCells: number, spanCols: number): ColSpan | null {
    const startCol = this.fullPressCol(pressBeat);
    const endCol = startCol + spanCols;
    if (!this.canPlaceSpan(startCol, endCol)) {
      return null;
    }
    paintFloor(this.grid, startCol, endCol, JUMP_GROUND_ROWS + heightCells);
    return { startCol, endCol };
  }

  /** Raise an explicit `[startCol, endCol)` to `heightCells` above baseline (for stairs). */
  raiseFloor(startCol: number, endCol: number, heightCells: number): boolean {
    if (!this.canPlaceSpan(startCol, endCol)) {
      return false;
    }
    paintFloor(this.grid, startCol, endCol, JUMP_GROUND_ROWS + heightCells);
    return true;
  }

  /** Paint a bare ceiling slab `ceilRows` deep across `[startCol, endCol)`. */
  placeCeiling(startCol: number, endCol: number, ceilRows: number): void {
    // Column 0 is the reserved spawn — never roof it (mirrors the other stamps).
    const start = Math.max(1, startCol);
    const end = Math.min(this.cols, endCol);
    if (end <= start) {
      return;
    }
    paintCeiling(this.grid, start, end, ceilRows);
  }

  /**
   * Carve a pit `widthCols` wide centred so a full-jump press at `pressBeat`
   * arcs over it, but only if there's a clear flat run-up and landing on both
   * sides (and never at the spawn). Returns the carved span or `null`.
   */
  pit(pressBeat: number, widthCols: number): ColSpan | null {
    const centreCol = this.fullPressCol(pressBeat);
    const startCol = centreCol - Math.floor(widthCols / 2);
    const endCol = startCol + widthCols;
    const marginCols = Math.ceil(PIT_EDGE_MARGIN_BEATS / this.cellBeats);
    if (startCol - marginCols < 1 || endCol + marginCols >= this.cols) {
      return null;
    }
    if (!this.isClearStrip(startCol - marginCols, endCol + marginCols)) {
      return null;
    }
    paintFloor(this.grid, startCol, endCol, 0);
    return { startCol, endCol };
  }
}
